import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Check, Ship, Car, Anchor, ShoppingBag, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Container, AnchorPills, CtaBand, ArrowLink } from "@/components/shared/PageHeader";
import { FadeUp } from "@/components/shared/FadeUp";
import { fetchContent, fetchSite } from "@/lib/api";
import { paragraphs } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Seo } from "@/components/shared/Seo";
import { BlockPhotos } from "@/components/shared/BlockPhotos";

const Prose = ({ text }) => <div className="prose-nn text-[17px] leading-relaxed text-foreground/85 text-pretty">{paragraphs(text).map((p, i) => <p key={i}>{p}</p>)}</div>;

/**
 * One chapter of the area page: picture (or any other visual passed as `visual`) on one side, text on the other.
 * Chapters alternate sides and are separated by a hairline, like pages in a guide.
 */
const Block = ({ id, c, reverse = false, visual, children }) => (
  <section id={id} className="scroll-mt-24 border-t border-border py-12 first:border-t-0 sm:py-16" data-testid={`area-section-${id}`}>
    <div className="grid gap-8 lg:grid-cols-12 lg:items-start lg:gap-8">
      <FadeUp className={cn("lg:col-span-7", reverse && "lg:order-2 lg:col-start-6")}>
        {visual || <BlockPhotos c={c} testId={`area-photos-${id}`} />}
      </FadeUp>
      <FadeUp delay={0.06} className={cn("lg:col-span-4", reverse ? "lg:order-1 lg:col-start-1" : "lg:col-start-9")}>
        <h2 className="t-h2 text-balance">{c?.title}</h2>
        <div className="mt-6"><Prose text={c?.body} /></div>
        {children}
      </FadeUp>
    </div>
  </section>
);

/** Live picture of Sæbøvik – the house borrows Fjord Ferie's camera until it has its own. Everything comes from site settings. */
const Webcam = ({ site }) => {
  if (!site?.webcam_enabled || !site?.webcam_youtube_id) return null;
  const src = `https://www.youtube.com/embed/${site.webcam_youtube_id}?rel=0&modestbranding=1&autoplay=1&mute=1&playsinline=1`;
  return (
    <section id="webkamera" className="scroll-mt-24 border-t border-border py-12 sm:py-16" data-testid="area-section-webkamera">
      <div className="grid gap-8 lg:grid-cols-12 lg:items-start lg:gap-8">
        <FadeUp className="lg:col-span-7">
          <figure className="relative overflow-hidden rounded-[20px] bg-ink ring-1 ring-foreground/10" data-testid="area-webcam">
            <div className="aspect-video w-full">
              <iframe title="Direktebilete frå Sæbøvik" src={src} className="h-full w-full border-0" loading="lazy" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" data-testid="area-webcam-iframe" />
            </div>
            <span className="pointer-events-none absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-ink/80 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-paper backdrop-blur" data-testid="area-webcam-live"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-sol" aria-hidden="true" />Direkte</span>
          </figure>
          {site.webcam_credit && (
            <p className="mt-3 text-[13px] text-muted-foreground" data-testid="area-webcam-credit">
              Kamera: {site.webcam_credit_url ? <a href={site.webcam_credit_url} target="_blank" rel="noopener noreferrer" className="font-medium text-foreground u-link">{site.webcam_credit}</a> : <span className="font-medium text-foreground">{site.webcam_credit}</span>} · Sæbøvik, Halsnøy
            </p>
          )}
        </FadeUp>
        <FadeUp delay={0.06} className="lg:col-span-4 lg:col-start-9">
          <p className="t-mono flex items-center gap-2 text-muted-foreground"><Radio className="h-3.5 w-3.5" /> Webkamera</p>
          <h2 className="t-h2 mt-2 text-balance">Sæbøvik akkurat no</h2>
          <div className="mt-6"><Prose text={["Sjå korleis veret og sjøen er på Halsnøy før du reiser – direkte frå hamna i Sæbøvik, like ved kulturhuset.", "Kameraet er lånt av Fjord Ferie fram til huset får sitt eige."].join("\n\n")} /></div>
          {site.webcam_timelapse_url && <div className="mt-6"><ArrowLink href={site.webcam_timelapse_url} target="_blank" rel="noopener noreferrer" up data-testid="area-webcam-timelapse-link">Sjå siste 30 dagar i tidsforløp</ArrowLink></div>}
        </FadeUp>
      </div>
    </section>
  );
};

export default function Area() {
  const { hash } = useLocation();
  const { data: content } = useQuery({ queryKey: ["content"], queryFn: fetchContent });
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: fetchSite });
  useEffect(() => {
    if (hash && content) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  }, [hash, content]);

  const bubil = content?.bubil;
  const email = site?.contact_email || "post@halsnoykulturhus.no";
  const mapsUrl = "https://www.google.com/maps/search/?api=1&query=Halsn%C3%B8y+Kulturhus+S%C3%A6b%C3%B8vik";
  const embedUrl = "https://www.google.com/maps?q=Halsn%C3%B8y%20Kulturhus%2C%20S%C3%A6b%C3%B8vik%2C%20Kvinnherad&z=12&output=embed";

  // «Slik kjem du hit» – the practical facts, taken from the chapter text.
  const howToGetHere = [
    { icon: Ship, text: "Ferje Ranavik–Skjersholmane (Stord)" },
    { icon: Car, text: "Halsnøytunnelen knyter øya til fastlandet" },
    { icon: Anchor, text: "Båthamn og gjestebrygge i Sæbøvik" },
    { icon: ShoppingBag, text: "Butikk og bubilplass like ved huset" },
  ];

  return (
    <div data-testid="area-page">
      <Seo title="Området – Sæbøvik og Halsnøy" description="Kulturhuset ligg midt i Sæbøvik på Halsnøy, med båthamn og gjestebrygge, bubilparkering med sanitærrom, leikeplass, butikk og turløyper like utanfor døra." image={content?.omradet?.images?.[0]} imageAlt="Sæbøvik og kulturhuset sett frå lufta" />
      <PageHeader
        eyebrow="Området"
        title="Sæbøvik, Halsnøy og alt rundt huset"
        intro="Turløyper, leikeplass, bubilparkering og båthamn – kulturhuset ligg midt i det. Her er det du treng å vite som besøkande."
        image={content?.omradet?.images?.[0]}
      >
        <AnchorPills testPrefix="area-anchor" items={[["#omradet", "Sæbøvik og Halsnøy"], ...(site?.webcam_enabled && site?.webcam_youtube_id ? [["#webkamera", "Webkamera"]] : []), ["#turloyper", "Turløyper"], ["#leikeplass", "Leikeplass"], ["#bubil", "Bubilparkering"], ["#bathamna", "Båthamna"]]}>
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-10 items-center gap-1.5 rounded-[8px] bg-foreground px-4 text-[14px] font-medium text-background transition-colors hover:bg-foreground/85" data-testid="area-maps-link">Vis i kart <ArrowUpRight className="h-3 w-3" /></a>
        </AnchorPills>
      </PageHeader>

      <Container>
        {/* The aerial photograph already opens the page – this chapter gets the map instead of the same picture twice. */}
        <Block
          id="omradet"
          c={content?.omradet}
          visual={
            <div className="overflow-hidden rounded-[20px] bg-secondary ring-1 ring-foreground/10" data-testid="area-map">
              <iframe title="Kart over Sæbøvik og Halsnøy" src={embedUrl} className="aspect-[16/10] w-full border-0 grayscale-[35%]" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
            </div>
          }
        >
          <div className="mt-8" data-testid="area-how-to-get-here">
            <p className="t-mono text-muted-foreground">Slik kjem du hit</p>
            <ul className="mt-2">
              {howToGetHere.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-center gap-3 rule-b py-3 text-[15px] last:border-b-0"><Icon className="h-4 w-4 shrink-0 text-muted-foreground" />{text}</li>
              ))}
            </ul>
            <div className="mt-5"><ArrowLink href={mapsUrl} target="_blank" rel="noopener noreferrer" up data-testid="area-maps-inline-link">Opne i Google Maps</ArrowLink></div>
          </div>
        </Block>
        <Webcam site={site} />
        <Block id="turloyper" c={content?.turloyper} reverse />
        <Block id="leikeplass" c={content?.leikeplass} />
        <Block id="bubil" c={bubil} reverse>
          <div className="mt-8" data-testid="bubil-prices">
            <p className="t-mono text-muted-foreground">Prisar</p>
            <ul className="mt-2 rounded-[16px] border border-border bg-card px-4">
              {(bubil?.meta?.prices || []).map((p, i) => (
                <li key={i} className="flex items-baseline justify-between gap-4 rule-b py-3 text-[15px] last:border-b-0"><span>{p.label}</span><span className="font-medium tabular">{p.price}</span></li>
              ))}
              {!(bubil?.meta?.prices || []).length && <li className="py-3 text-sm text-muted-foreground">Prisar kjem.</li>}
            </ul>
          </div>
          <div className="mt-6" data-testid="bubil-rules">
            <p className="t-mono text-muted-foreground">Reglar</p>
            <ul className="mt-2 space-y-2 text-[15px]">
              {(bubil?.meta?.rules || []).map((r, i) => <li key={i} className="flex items-start gap-2.5"><Check className="mt-1 h-4 w-4 shrink-0" />{r}</li>)}
            </ul>
          </div>
          <div className="mt-6"><ArrowLink href={`mailto:${email}?subject=Bubilparkering`} data-testid="bubil-contact-link">Spør om bubilplass</ArrowLink></div>
        </Block>
        <Block id="bathamna" c={content?.bathamna}>
          <p className="mt-6 text-[15px] text-muted-foreground">Treng du lokale til årsmøte eller samling for båtlaget? <Link to="/leig-rom" className="font-medium text-foreground u-link">Book eit rom</Link>.</p>
        </Block>
      </Container>

      <CtaBand eyebrow="Velkomen til øya" title="Kombiner turen med eit arrangement på kulturhuset" text="Kurs, konsert eller familiefest – vi har rom for det meste, med sjøen som næraste nabo.">
        <Button asChild variant="light" size="lg" data-testid="area-booking-link"><Link to="/leig-rom">Leig rom <ArrowUpRight className="h-4 w-4" /></Link></Button>
        <Button asChild variant="lightOutline" size="lg"><Link to="/rom">Sjå romma</Link></Button>
      </CtaBand>
    </div>
  );
}
